/**
 * useAccountRealtimeSync - Account-level SSE connection
 *
 * Subscribes to events routed via account:{account_id} rather than
 * a specific client_id. Used by the leads page and future org-level
 * features to receive events like lead_enriched.
 *
 * Usage:
 *   const { on } = useAccountRealtimeSync();
 *   on("lead_enriched", (data) => refetchLeads());
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { API_CONFIG } from "@/config/api";

const API_URL = API_CONFIG.URL;

type EventHandler = (data: Record<string, unknown>) => void;

interface AccountEvent {
  type: string;
  data?: Record<string, unknown>;
  timestamp?: string;
}

const getAccessToken = (): string | null => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

export function useAccountRealtimeSync() {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const handlersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const on = useCallback((eventType: string, handler: EventHandler) => {
    if (!handlersRef.current.has(eventType)) {
      handlersRef.current.set(eventType, new Set());
    }
    handlersRef.current.get(eventType)!.add(handler);

    return () => {
      handlersRef.current.get(eventType)?.delete(handler);
    };
  }, []);

  const connect = useCallback(() => {
    if (typeof EventSource === "undefined") return;

    const accessToken = getAccessToken();
    if (!accessToken) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = new URL(`${API_URL}/api/account/realtime/stream`);
    url.searchParams.append("access_token", accessToken);

    try {
      const es = new EventSource(url.toString());
      eventSourceRef.current = es;

      es.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      es.onmessage = (event) => {
        try {
          const parsed: AccountEvent = JSON.parse(event.data);
          const handlers = handlersRef.current.get(parsed.type);
          if (handlers && parsed.data) {
            handlers.forEach((h) => h(parsed.data!));
          }
        } catch {
          // ignore parse errors
        }
      };

      es.onerror = () => {
        setIsConnected(false);
        es.close();
        eventSourceRef.current = null;

        if (reconnectAttemptsRef.current >= 5) return;

        reconnectAttemptsRef.current += 1;
        const backoff = Math.min(
          1000 * Math.pow(2, reconnectAttemptsRef.current - 1),
          30000
        );

        reconnectTimeoutRef.current = setTimeout(connect, backoff);
      };
    } catch {
      setIsConnected(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.hidden) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Pause the stream while the tab is hidden so a backgrounded tab
  // doesn't hold a Cloud Run request slot open for the full 5-min
  // platform timeout.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (eventSourceRef.current || reconnectTimeoutRef.current) {
          disconnect();
        }
      } else if (!eventSourceRef.current) {
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect, disconnect]);

  return { isConnected, on };
}
